import React, { useCallback, FC } from 'react';
import _ from 'lodash';
import { BlockType } from '@tradingblock/types';
import { Svg, GetTradingBlockSiteName } from '@tradingblock/components';
import { useDispatcher } from '../../data/global/hooks';
import {
  useStateSelector,
  blocksSelectors,
  isMarketOpenSelector,
} from '../../data/global/dataSelectors';
import { useFeatureFlag } from '@tradingblock/components';

const TradingStatusIconButton: React.FC<{ cssClasses?: string; title?: string; icon: string }> = ({
  title,
  cssClasses,
  icon,
}) => {
  return (
    <button className="dashboard-nav-tool dashboard-nav-tool-btn" type="button" onClick={() => { }}>
      <span className={`tb-icon tb-icon-2x tb-icon-stack tb-icon-clipboard ${cssClasses || ''}`} title={title}>
        <span className="tb-icon tb-icon-stack-2x">
          <Svg path="icon-clipboard" />
        </span>
        <span className="tb-icon tb-icon-stack-1x">
          <Svg path={`icon-${icon}`} />
        </span>
      </span>
    </button>
  );
};

export const TradingStatusIcon: FC<{ onAddBlock: (type: BlockType) => void }> = ({ onAddBlock }) => {
  const marketOpen = useStateSelector(s => isMarketOpenSelector(s));
  const blocks = useStateSelector(s => blocksSelectors.blocks(s));
  const feedInfo = useStateSelector(s => {
    const { status, message, isReconnecting } = s.feedProvider;
    const isClosed = status === 'closed';
    const isConnected = status === 'open';
    const isError = status === 'error' ? true : isClosed && isReconnecting === false ? true : false;

    return { status, message, isConnected, isError, isWarning: isClosed, isPending: status === undefined };
  });

  // Check if old WebSocket is needed based on feature flags (same logic as WebSocketInitializer)
  const { isEnabled: useNewQuoteService } = useFeatureFlag('new_quote_service');
  const { isEnabled: useNewSpreadService } = useFeatureFlag('new_spread_service');
  const { isEnabled: useLofiOrderUpdates } = useFeatureFlag('lofi-order-updates');
  const { isEnabled: useLofiCashiering } = useFeatureFlag('lofi-cashiering');
  const { isEnabled: useLofiExpiringTokens } = useFeatureFlag('lofi-expiring-tokens');

  // Old WebSocket is needed if:
  // - New quote service is disabled OR
  // - New spread service is disabled OR
  // - Order updates use old WebSocket (lofi-order-updates is false) OR
  // - Cashiering uses old WebSocket (lofi-cashiering is false) OR
  // - Expired tokens use old WebSocket (lofi-expiring-tokens is false)
  const isOldWebSocketNeeded = !useNewQuoteService || !useNewSpreadService || !useLofiOrderUpdates || !useLofiCashiering || !useLofiExpiringTokens;

  const { dispatcher } = useDispatcher();
  const onAddOrHighlightBlockType = useCallback(
    (blockType: BlockType) => {
      if (_.some(blocks, b => b.type === blockType)) {
        // highlight all blocks of same type
        dispatcher.block.highlightBlocks(blockType);
      } else {
        // add new block
        onAddBlock(blockType);
      }
    },
    [blocks, dispatcher.block, onAddBlock]
  );

  const cssClasses = feedInfo.isError ? 'neg' : feedInfo.isWarning ? 'warn' : feedInfo.isPending ? 'pending' : '';
  const title = feedInfo.message
    ? feedInfo.message
    : feedInfo.isPending
      ? 'Connecting...'
      : `Cannot connect to ${GetTradingBlockSiteName()} API`;

  if (isOldWebSocketNeeded && (feedInfo.isError || feedInfo.isWarning || feedInfo.isPending)) {
    return (
      <TradingStatusIconButton icon="lightning" cssClasses={cssClasses} title={title} />
    );
  }

  return (
    <>
      <button
        className="dashboard-nav-tool dashboard-nav-tool-btn"
        type="button"
        onClick={() => onAddOrHighlightBlockType(BlockType.Order)}
      >
        <span
          className="tb-icon tb-icon-2x tb-icon-stack tb-icon-clipboard"
          title={marketOpen ? 'Trading open' : 'Trading closed'}
        >
          <span className="tb-icon tb-icon-stack-2x">
            <Svg path="icon-clipboard" />
          </span>
          <span className="tb-icon tb-icon-stack-1x">
            <Svg path={marketOpen ? 'icon-trading' : 'icon-night'} />
          </span>
        </span>
      </button>
    </>
  );
};
